import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { adminApi, teamApi } from '@/api/endpoints';
import type { AdminMatchday, AnswerType, Question, Team } from '@/api/types';
import { Alert, Button, Card, Spinner } from '@/components/ui';
import { useAsync } from '@/lib/useAsync';
import styles from './admin.module.css';

interface LoadedData {
  matchday: AdminMatchday | null;
  questions: Question[];
  teams: Team[];
  /** Alle Teams (inkl. Pokal-Gegner) — nur um bestehende Lösungen aufzulösen. */
  allTeams: Team[];
}

/**
 * Dropdown-Liste einer Frage: Liga-Teams plus bereits als Lösung gespeicherte
 * Teams, die nicht (mehr) in der Liga-Liste stehen (z. B. ein früher gewählter
 * Pokal-Gegner) — sonst zeigt das Dropdown statt der Lösung eine leere Auswahl.
 */
function teamsForQuestion(q: Question, leagueTeams: Team[], allTeams: Team[]): Team[] {
  const referenced = [q.correct_team_id, ...(q.correct_ranking_team_ids ?? [])].filter(
    (id): id is number => id != null,
  );
  const missing = referenced
    .filter((id) => !leagueTeams.some((t) => t.id === id))
    .map((id) => allTeams.find((t) => t.id === id))
    .filter((t): t is Team => t !== undefined);
  return missing.length > 0 ? [...leagueTeams, ...missing] : leagueTeams;
}

const MIN_SLOTS = 2;
const MAX_SLOTS = 6;
const MIN_OPTIONS = 2;
const MAX_OPTIONS = 12;

/**
 * Admin: Fragen eines Spieltags verwalten (§14.3, generisch für JEDEN Spieltag,
 * nicht nur kind='bonus') — anlegen, bearbeiten, löschen und die correct-Antwort
 * setzen ⇒ löst Bewertung aus. Bei answer_type='team_ranking' (z. B. "Welche
 * Mannschaften belegen die Plätze 1-3?") ein Team-Dropdown je Platz statt eines
 * einzelnen; bei 'multiple_choice' frei formulierte Antwortoptionen mit
 * Checkbox je richtiger Option.
 */
export function AdminQuestionsPage() {
  const { id } = useParams<{ id: string }>();
  const matchdayId = Number(id);

  const { data, loading, error, reload } = useAsync<LoadedData>(
    async (signal) => {
      const [matchdays, questions, teams, allTeams] = await Promise.all([
        adminApi.listMatchdays(signal),
        adminApi.listQuestions(matchdayId, signal),
        teamApi.listLeague(signal).catch(() => [] as Team[]),
        teamApi.list(signal).catch(() => [] as Team[]),
      ]);
      return {
        matchday: matchdays.find((m) => m.id === matchdayId) ?? null,
        questions: [...questions].sort((a, b) => a.sort_order - b.sort_order),
        teams,
        allTeams,
      };
    },
    [matchdayId],
  );

  if (Number.isNaN(matchdayId)) return <Alert>Ungültiger Spieltag.</Alert>;
  if (loading) return <Spinner label="Fragen werden geladen…" />;
  if (error) return <Alert>{error}</Alert>;

  const matchday = data?.matchday ?? null;
  const questions = data?.questions ?? [];
  const teams = data?.teams ?? [];
  const allTeams = data?.allTeams ?? [];
  const isBonus = matchday?.kind === 'bonus';

  return (
    <>
      <p className="muted">
        <Link to="/admin/matchdays">← Spieltage</Link>
      </p>
      <h2>
        {isBonus ? 'Bonusfragen' : 'Fragen'}
        {matchday ? ` — ${matchday.name}` : ''}
      </h2>

      <CreateQuestionForm matchdayId={matchdayId} onCreated={reload} />

      {questions.length === 0 ? (
        <Card>
          <p className="muted">Noch keine Fragen. Lege oben eine an.</p>
        </Card>
      ) : (
        questions.map((q) => (
          <QuestionCard
            key={q.id}
            question={q}
            teams={teamsForQuestion(q, teams, allTeams)}
            onChanged={reload}
          />
        ))
      )}
    </>
  );
}

function CreateQuestionForm({
  matchdayId,
  onCreated,
}: {
  matchdayId: number;
  onCreated: () => void;
}) {
  const [prompt, setPrompt] = useState('');
  const [shortCode, setShortCode] = useState('');
  const [answerType, setAnswerType] = useState<AnswerType>('team');
  const [points, setPoints] = useState('3');
  const [slotCount, setSlotCount] = useState('3');
  const [options, setOptions] = useState<{ label: string; shortCode: string }[]>([
    { label: '', shortCode: '' },
    { label: '', shortCode: '' },
  ]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);

  const isRanking = answerType === 'team_ranking';
  const isMultipleChoice = answerType === 'multiple_choice';
  const filledOptions = options.filter((o) => o.label.trim() !== '');

  function updateOptionLabel(index: number, value: string) {
    setOptions((prev) => prev.map((o, i) => (i === index ? { ...o, label: value } : o)));
  }

  function updateOptionShortCode(index: number, value: string) {
    setOptions((prev) => prev.map((o, i) => (i === index ? { ...o, shortCode: value } : o)));
  }

  function addOption() {
    setOptions((prev) =>
      prev.length >= MAX_OPTIONS ? prev : [...prev, { label: '', shortCode: '' }],
    );
  }

  function removeOption(index: number) {
    setOptions((prev) => (prev.length <= MIN_OPTIONS ? prev : prev.filter((_, i) => i !== index)));
  }

  async function submit() {
    setBusy(true);
    setError(null);
    setSaved(null);
    try {
      await adminApi.createQuestion(matchdayId, {
        prompt: prompt.trim(),
        short_code: shortCode.trim() === '' ? undefined : shortCode.trim(),
        answer_type: answerType,
        points: points === '' ? undefined : Number(points),
        slot_count: isRanking ? Number(slotCount) : undefined,
        options: isMultipleChoice
          ? filledOptions.map((o) => ({
              label: o.label.trim(),
              short_code: o.shortCode.trim() === '' ? undefined : o.shortCode.trim(),
            }))
          : undefined,
      });
      setSaved('Frage angelegt.');
      setPrompt('');
      setShortCode('');
      setPoints('3');
      setSlotCount('3');
      setOptions([
        { label: '', shortCode: '' },
        { label: '', shortCode: '' },
      ]);
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Anlegen fehlgeschlagen.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <h2>Frage anlegen</h2>
      <div className={styles.inlineForm}>
        <label className={styles.field} style={{ marginBottom: 0, flex: '1 1 220px' }}>
          <span className={styles.label}>Frage</span>
          <input
            className={styles.input}
            value={prompt}
            placeholder="z. B. Wer wird Herbstmeister?"
            onChange={(e) => setPrompt(e.target.value)}
          />
        </label>
        <label className={styles.field} style={{ marginBottom: 0, flex: '0 0 90px' }}>
          <span className={styles.label}>Kürzel</span>
          <input
            className={styles.input}
            value={shortCode}
            placeholder="z. B. HM"
            maxLength={10}
            onChange={(e) => setShortCode(e.target.value)}
          />
        </label>
        <label className={styles.field} style={{ marginBottom: 0, flex: '0 0 170px' }}>
          <span className={styles.label}>Typ</span>
          <select
            className={styles.categorySelect}
            value={answerType}
            onChange={(e) => setAnswerType(e.target.value as AnswerType)}
          >
            <option value="team">Team (eine Antwort)</option>
            <option value="team_ranking">Team-Rangfolge (mehrere Plätze)</option>
            <option value="multiple_choice">Freie Antwortoptionen</option>
          </select>
        </label>
        {isRanking && (
          <label className={styles.field} style={{ marginBottom: 0, flex: '0 0 110px' }}>
            <span className={styles.label}>Plätze</span>
            <input
              className={styles.input}
              type="text"
              inputMode="numeric"
              value={slotCount}
              onChange={(e) => setSlotCount(e.target.value.replace(/\D/g, '').slice(0, 1))}
            />
          </label>
        )}
        <label className={styles.field} style={{ marginBottom: 0, flex: '0 0 90px' }}>
          <span className={styles.label}>{isRanking ? 'Punkte/Platz' : 'Punkte'}</span>
          <input
            className={styles.input}
            type="text"
            inputMode="numeric"
            value={points}
            onChange={(e) => setPoints(e.target.value.replace(/\D/g, '').slice(0, 2))}
          />
        </label>
        <Button
          className={styles.btnSm}
          disabled={
            busy ||
            prompt.trim() === '' ||
            (isRanking && (Number(slotCount) < MIN_SLOTS || Number(slotCount) > MAX_SLOTS)) ||
            (isMultipleChoice && filledOptions.length < MIN_OPTIONS)
          }
          onClick={() => void submit()}
        >
          {busy ? '…' : 'Anlegen'}
        </Button>
      </div>
      {isRanking && (
        <p className="muted" style={{ marginTop: 8 }}>
          {MIN_SLOTS}–{MAX_SLOTS} Plätze. Punkte werden PRO richtig platziertem Team vergeben (z. B.
          3 Punkte × 3 Plätze = max. 9).
        </p>
      )}
      {isMultipleChoice && (
        <div className={styles.deadlineForm} style={{ flexDirection: 'column', alignItems: 'stretch' }}>
          {options.map((opt, i) => (
            <div key={i} className={styles.inlineForm} style={{ marginBottom: 0 }}>
              <label className={styles.field} style={{ marginBottom: 0, flex: '1 1 auto' }}>
                <span className={styles.label}>Option {i + 1}</span>
                <input
                  className={styles.input}
                  value={opt.label}
                  placeholder="z. B. FC St. Pauli"
                  onChange={(e) => updateOptionLabel(i, e.target.value)}
                />
              </label>
              <label className={styles.field} style={{ marginBottom: 0, flex: '0 0 90px' }}>
                <span className={styles.label}>Kürzel</span>
                <input
                  className={styles.input}
                  value={opt.shortCode}
                  placeholder="z. B. FCB"
                  maxLength={10}
                  onChange={(e) => updateOptionShortCode(i, e.target.value)}
                />
              </label>
              <Button
                variant="ghost"
                className={`${styles.btnSm} ${styles.btnDanger}`}
                disabled={options.length <= MIN_OPTIONS}
                onClick={() => removeOption(i)}
              >
                Entfernen
              </Button>
            </div>
          ))}
          <Button
            variant="ghost"
            className={styles.btnSm}
            disabled={options.length >= MAX_OPTIONS}
            onClick={addOption}
          >
            + Option hinzufügen
          </Button>
          <p className="muted" style={{ marginTop: 0 }}>
            {MIN_OPTIONS}–{MAX_OPTIONS} Optionen. Beim Speichern der Lösung können mehrere Optionen
            als richtig markiert werden.
          </p>
        </div>
      )}
      {error && (
        <div style={{ marginTop: 8 }}>
          <Alert>{error}</Alert>
        </div>
      )}
      {saved && <div className={styles.savedHint}>{saved}</div>}
    </Card>
  );
}

function QuestionCard({
  question,
  teams,
  onChanged,
}: {
  question: Question;
  teams: Team[];
  onChanged: () => void;
}) {
  const [prompt, setPrompt] = useState(question.prompt);
  const [shortCode, setShortCode] = useState(question.short_code ?? '');
  const [points, setPoints] = useState(String(question.points));
  const [correct, setCorrect] = useState(
    question.correct_team_id != null ? String(question.correct_team_id) : '',
  );
  const slotCount = question.slot_count ?? 0;
  const [rankingCorrect, setRankingCorrect] = useState<string[]>(() => {
    const existing = question.correct_ranking_team_ids ?? [];
    return Array.from({ length: slotCount }, (_, i) => {
      const v = existing[i];
      return v != null ? String(v) : '';
    });
  });
  const [optionsCorrect, setOptionsCorrect] = useState<Set<number>>(
    () => new Set(question.correct_option_ids ?? []),
  );
  const [optionShortCodes, setOptionShortCodes] = useState<Record<number, string>>(() =>
    Object.fromEntries((question.options ?? []).map((o) => [o.id, o.short_code ?? ''])),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);

  const isRanking = question.answer_type === 'team_ranking';
  const isMultipleChoice = question.answer_type === 'multiple_choice';

  function toggleOptionCorrect(optionId: number) {
    setOptionsCorrect((prev) => {
      const next = new Set(prev);
      if (next.has(optionId)) {
        next.delete(optionId);
      } else {
        next.add(optionId);
      }
      return next;
    });
  }

  async function patch(input: Parameters<typeof adminApi.patchQuestion>[1], msg: string) {
    setBusy(true);
    setError(null);
    setSaved(null);
    try {
      await adminApi.patchQuestion(question.id, input);
      setSaved(msg);
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Speichern fehlgeschlagen.');
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!window.confirm('Frage wirklich löschen?')) return;
    setBusy(true);
    setError(null);
    try {
      await adminApi.deleteQuestion(question.id);
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Löschen fehlgeschlagen.');
      setBusy(false);
    }
  }

  const saveButton = (
    <Button
      className={styles.btnSm}
      disabled={busy || prompt.trim() === ''}
      onClick={() =>
        void patch(
          {
            prompt: prompt.trim(),
            short_code: shortCode.trim() === '' ? null : shortCode.trim(),
            points: points === '' ? undefined : Number(points),
            option_short_codes: Object.fromEntries(
              Object.entries(optionShortCodes).map(([id, code]) => [
                Number(id),
                code.trim() === '' ? null : code.trim(),
              ]),
            ),
          },
          'Gespeichert.',
        )
      }
    >
      Speichern
    </Button>
  );
  const deleteButton = (
    <Button
      variant="ghost"
      className={`${styles.btnSm} ${styles.btnDanger}`}
      disabled={busy}
      onClick={() => void remove()}
    >
      Löschen
    </Button>
  );

  return (
    <Card>
      <div className={styles.inlineForm}>
        <label className={styles.field} style={{ marginBottom: 0, flex: '1 1 220px' }}>
          <span className={styles.label}>Frage</span>
          <input
            className={styles.input}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
          />
        </label>
        <label className={styles.field} style={{ marginBottom: 0, flex: '0 0 90px' }}>
          <span className={styles.label}>Kürzel</span>
          <input
            className={styles.input}
            value={shortCode}
            placeholder="z. B. HM"
            maxLength={10}
            onChange={(e) => setShortCode(e.target.value)}
          />
        </label>
        <label className={styles.field} style={{ marginBottom: 0, flex: '0 0 90px' }}>
          <span className={styles.label}>{isRanking ? 'Punkte/Platz' : 'Punkte'}</span>
          <input
            className={styles.input}
            type="text"
            inputMode="numeric"
            value={points}
            onChange={(e) => setPoints(e.target.value.replace(/\D/g, '').slice(0, 2))}
          />
        </label>
      </div>

      {isMultipleChoice ? (
        <div
          className={styles.deadlineForm}
          style={{ flexDirection: 'column', alignItems: 'stretch' }}
        >
          <span className={styles.label}>Richtige Option(en) (löst Bewertung aus)</span>
          {(question.options ?? []).map((opt) => (
            <div key={opt.id} className={styles.inlineForm} style={{ marginBottom: 0 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 400, flex: '1 1 auto' }}>
                <input
                  type="checkbox"
                  checked={optionsCorrect.has(opt.id)}
                  onChange={() => toggleOptionCorrect(opt.id)}
                />
                {opt.label}
              </label>
              <label className={styles.field} style={{ marginBottom: 0, flex: '0 0 90px' }}>
                <span className={styles.label}>Kürzel</span>
                <input
                  className={styles.input}
                  value={optionShortCodes[opt.id] ?? ''}
                  placeholder="z. B. FCB"
                  maxLength={10}
                  onChange={(e) =>
                    setOptionShortCodes((prev) => ({ ...prev, [opt.id]: e.target.value }))
                  }
                />
              </label>
            </div>
          ))}
          <div className={styles.inlineForm} style={{ marginBottom: 0 }}>
            {saveButton}
            {deleteButton}
            <Button
              className={styles.btnSm}
              disabled={busy}
              onClick={() =>
                void patch(
                  { correct_option_ids: Array.from(optionsCorrect) },
                  'Lösung gesetzt & bewertet.',
                )
              }
            >
              Lösung setzen
            </Button>
          </div>
        </div>
      ) : isRanking ? (
        <div
          className={styles.deadlineForm}
          style={{ flexDirection: 'column', alignItems: 'stretch' }}
        >
          {Array.from({ length: slotCount }, (_, i) => (
            <label key={i} className={styles.field} style={{ marginBottom: 0 }}>
              <span className={styles.label}>Platz {i + 1}</span>
              <select
                className={styles.categorySelect}
                value={rankingCorrect[i] ?? ''}
                onChange={(e) =>
                  setRankingCorrect((prev) => {
                    const next = [...prev];
                    next[i] = e.target.value;
                    return next;
                  })
                }
              >
                <option value="">— noch offen —</option>
                {teams.map((t) => (
                  <option key={t.id} value={String(t.id)}>
                    {t.name}
                  </option>
                ))}
              </select>
            </label>
          ))}
          <div className={styles.inlineForm} style={{ marginBottom: 0 }}>
            {saveButton}
            {deleteButton}
            <Button
              className={styles.btnSm}
              disabled={busy || rankingCorrect.some((v) => v === '')}
              onClick={() =>
                void patch(
                  { correct_ranking_team_ids: rankingCorrect.map((v) => Number(v)) },
                  'Lösung gesetzt & bewertet.',
                )
              }
            >
              Lösung setzen
            </Button>
          </div>
        </div>
      ) : (
        <div className={styles.deadlineForm}>
          <label className={styles.field} style={{ marginBottom: 0, flex: '1 1 200px' }}>
            <span className={styles.label}>Richtige Antwort (löst Bewertung aus)</span>
            <select
              className={styles.categorySelect}
              value={correct}
              onChange={(e) => setCorrect(e.target.value)}
            >
              <option value="">— noch offen —</option>
              {teams.map((t) => (
                <option key={t.id} value={String(t.id)}>
                  {t.name}
                </option>
              ))}
            </select>
          </label>
          {saveButton}
          {deleteButton}
          <Button
            className={styles.btnSm}
            disabled={busy || correct === ''}
            onClick={() =>
              void patch({ correct_team_id: Number(correct) }, 'Lösung gesetzt & bewertet.')
            }
          >
            Lösung setzen
          </Button>
        </div>
      )}

      {error && (
        <div style={{ marginTop: 8 }}>
          <Alert>{error}</Alert>
        </div>
      )}
      {saved && <div className={styles.savedHint}>{saved}</div>}
    </Card>
  );
}
